import { useEffect, useRef, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { Check, ArrowRight } from "lucide-react";
import { api } from "@/lib/api";
import { useCart, fmtPrice } from "@/lib/cart";
import { Reveal } from "@/components/Reveal";

export default function OrderConfirmation() {
  const [params] = useSearchParams();
  const sessionId = params.get("session_id");
  const [order, setOrder] = useState(null);
  const [tries, setTries] = useState(0);
  const { clear } = useCart();
  const cleared = useRef(false);

  useEffect(() => {
    if (!sessionId) return;
    const t = setTimeout(() => {
      api
        .get(`/shop/order-status/${sessionId}`)
        .then((r) => {
          setOrder(r.data);
          setTries((n) => n + 1);
        })
        .catch(() => setTries((n) => n + 1));
    }, 1500);
    return () => clearTimeout(t);
  }, [sessionId, tries]);

  useEffect(() => {
    if (order?.payment_status === "paid" && !cleared.current) {
      cleared.current = true;
      clear();
    }
  }, [order, clear]);

  const paid = order?.payment_status === "paid";

  return (
    <div className="bg-cream min-h-[60vh]" data-testid="confirmation-page">
      <section className="container-site py-16 sm:py-24 max-w-2xl text-center">
        <Reveal>
          {!order || (!paid && tries < 12) ? (
            <div data-testid="confirmation-pending">
              <p className="label-eyebrow mb-4">Confirming your order</p>
              <h1 className="font-display font-black tracking-tight text-3xl sm:text-4xl text-ink">One moment<span className="text-pool">…</span></h1>
              <p className="mt-4 text-slate-500 text-sm">We&rsquo;re confirming your payment with Stripe.</p>
            </div>
          ) : paid ? (
            <div data-testid="confirmation-success">
              <div className="mx-auto h-16 w-16 rounded-full bg-pool flex items-center justify-center">
                <Check className="h-8 w-8 text-white" />
              </div>
              <h1 className="mt-6 font-display font-black tracking-tight text-3xl sm:text-4xl text-ink" data-testid="confirmation-headline">
                You&rsquo;re All <span className="text-pool">Set.</span>
              </h1>
              <p className="mt-4 text-slate-600 text-sm sm:text-base">
                Thank you, {order.customer?.name?.split(" ")[0]}! Your order <span className="font-bold text-ink" data-testid="confirmation-order-number">{order.order_number}</span> is confirmed.
                A receipt is on its way to {order.customer?.email}.
              </p>
              <div className="mt-8 bg-white rounded-2xl border border-slate-100 p-6 text-left" data-testid="confirmation-summary">
                {order.items.map((i, idx) => (
                  <div key={idx} className="flex items-center justify-between py-2 text-sm">
                    <p className="text-slate-600"><span className="font-semibold text-ink">{i.name}</span>{i.variant && <span className="text-slate-400"> · {i.variant}</span>} <span className="text-slate-400">× {i.quantity}</span></p>
                    <p className="font-semibold text-ink">{fmtPrice(i.unit_price_cents * i.quantity)}</p>
                  </div>
                ))}
                <div className="mt-3 pt-3 border-t border-slate-100 flex items-center justify-between">
                  <p className="text-xs tracking-[0.2em] uppercase font-bold text-slate-500">{order.fulfillment === "shipping" ? "Ships to you" : "Pickup at the Café"}</p>
                  <p className="font-display font-black text-xl text-ink" data-testid="confirmation-total">{fmtPrice(order.subtotal_cents)}</p>
                </div>
              </div>
              <Link to="/shop" className="mt-8 inline-flex items-center gap-2.5 bg-pool hover:bg-pool-dark text-white font-bold text-xs tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors" data-testid="confirmation-shop-link">
                Keep Shopping <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          ) : (
            <div data-testid="confirmation-unpaid">
              <h1 className="font-display font-black tracking-tight text-3xl text-ink">Payment not completed</h1>
              <p className="mt-4 text-slate-500 text-sm">Your payment wasn&rsquo;t completed. Your bag is still saved — you can try again anytime.</p>
              <Link to="/cart" className="mt-6 inline-flex items-center gap-2.5 bg-pool hover:bg-pool-dark text-white font-bold text-xs tracking-[0.15em] uppercase px-8 py-4 rounded-full transition-colors" data-testid="confirmation-cart-link">
                Back to Bag <ArrowRight className="h-4 w-4" />
              </Link>
            </div>
          )}
        </Reveal>
      </section>
    </div>
  );
}
